import type { ApiResponse } from '../types/api';
import type { User } from '../types/auth';
import type { UserLimits } from '../types/user';
import httpClient from './http/httpClient';

export interface UserFilters {
  role?: string;
  companyId?: string;
  status?: string;
  searchTerm?: string;
  limit?: number;
  offset?: number;
}

export interface UpdateUserData {
  userId: string;
  email?: string;
  displayName?: string;
  role?: string;
  permissions?: string[];
  status?: string;
  companyId?: string;
  teamId?: string;
  limits?: UserLimits;
  disabled?: boolean;
}

import type { KnowledgeBaseFolder } from './authService';

export interface ClientUpdate {
  clientName?: string | null;
  knowledgeBaseFolders?: KnowledgeBaseFolder[];
  settings?: {
    [key: string]: unknown;
  };
  enablePartnerSearch?: boolean;
  matchmakingCountryFilter?: string;
  matchmakingRegionFilter?: string;
  matchmakingMinScore?: number;
  enablePartnerVisibility?: boolean;
  failedMigrations?: string[] | null;
  saasAgreementVersion?: string;
  saasAgreementStatus?: 'signed' | 'pending_update' | 'unsigned';
  saasAgreementSignedAt?: string;
  clientSettings?: {
    enableExternalClientMode?: boolean;
    [key: string]: unknown;
  };
}

export interface TeamUpdate {
  company_legal_name?: string | null;
  company_registration?: string | null;
  secondary_company_registration?: string | null;
  company_address?: string | null;
  company_website?: string | null;
  company_phone?: string | null;
  timezone?: string | null;
  company_contact_name?: string | null;
  company_contact_email?: string | null;
  company_contact_role?: string | null;
  contact_facsimile?: string | null;
  liability_insurer?: string | null;
  liability_insurance_expiry?: string | null;
  liability_insurance_per_claim?: string | number | null;
  liability_insurance_aggregate?: string | number | null;
  indemnity_insurer?: string | null;
  indemnity_insurer_expiry?: string | null;
  indemnity_insurer_per_claim?: string | number | null;
  indemnity_insurer_aggregate?: string | number | null;
  workers_compensation_insurer_1?: string | null;
  workers_compensation_expiry_1?: string | null;
  workers_compensation_insurer_2?: string | null;
  workers_compensation_expiry_2?: string | null;
  workers_compensation_insurer_3?: string | null;
  workers_compensation_expiry_3?: string | null;
  workers_compensation_insurer_4?: string | null;
  workers_compensation_expiry_4?: string | null;
}

export interface ProposalHeading {
  id: string;
  title: string;
  enabled?: boolean;
  type: 'default' | 'custom';
}

export interface AISettings {
  includeScales?: boolean;
  includeStrengths?: boolean;
  includeLimitations?: boolean;
  includeStrategy?: boolean;
  includeCriteria?: boolean;
  otherAnalysisInstructions?: string;
  includeCVs?: boolean;
  draftWordLimit?: number;
  confidenceThreshold?: number;
  defaultModelName?: string;
  includeCaseStudies?: boolean;
  caseStudyMode?: 'off' | 'inline' | 'sections';
  minCaseStudies?: number;
  maxCaseStudies?: number;
  otherResponseInstructions?: string;
  includeDifReview?: boolean;
  includeSuggestions?: boolean;
  otherReviewInstructions?: string;
  showDashboardActionCards?: boolean;
  dismissedDashboardActions?: string[];
  lastUpdated?: string;
  draftFormatMode?: 'form_only' | 'narrative' | 'hybrid';
  enabledForms?: string[];
}

export interface AccountDetailsUpdate {
  client?: ClientUpdate;
  team?: TeamUpdate;
  aiSettings?: AISettings;
  clientLanguage?: 'US English' | 'UK English' | 'Australian English';
  customProposalStructure?: ProposalHeading[];

  publicKey?: JsonWebKey;
  encryptedPrivateKey?: string;
  encryptionParams?: unknown;
  recoveryParams?: unknown;

  preferences?: Record<string, unknown>;
  kmsMigratedAt?: string;
}

export interface UserResponse {
  success: boolean;
  users?: User[];
  user?: User;
  message?: string;
  total?: number;
  availableTokens?: number;
  userLimit?: number;
  currentUserCount?: number;
}

export interface PasswordResetResponse {
  success: boolean;
  message?: string;
  temporaryPassword?: string;
}

export const getPublicKey = async (userId: string): Promise<JsonWebKey> => {
  try {
    const response = await httpClient.get<{ success: boolean; publicKey: JsonWebKey }>(
      '/getPublicKey',
      {
        params: { userId },
      }
    );
    if (!response.success || !response.publicKey) {
      throw new Error('Public key not found.');
    }
    return response.publicKey;
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } }; message?: string };
    throw new Error(err.response?.data?.error || err.message || 'Failed to fetch public key.');
  }
};

export const linkPartnerRequest = async (tenderId: string, targetUserId: string): Promise<void> => {
  try {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      '/linkPartnerRequest',
      { targetUserId, tenderId }
    );
    if (!response.success) {
      throw new Error('Failed to send notification to partner.');
    }
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } }; message?: string };
    throw new Error(err.response?.data?.error || err.message || 'Failed to notify partner.');
  }
};

export interface PartnerInvitationData {
  tenderId: string;
  title: string;
  description: string;
  clientName: string;
  partnerName: string;
  valueAdd: string;
  matchScore: number;
  inviterName: string;
  inviterEmail: string;
  inviterPhone?: string;
  inviterWebsite?: string;
  inviterPartnerId?: string | null;
}

export interface GetPartnerInvitationResponse {
  success: boolean;
  invitation?: PartnerInvitationData;
  status?: string;
  error?: string;
}

export const getPartnerInvitation = async (
  tenderId: string
): Promise<GetPartnerInvitationResponse> => {
  try {
    const response = await httpClient.post<GetPartnerInvitationResponse>('/getPartnerInvitation', {
      tenderId,
    });
    return response;
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } }; message?: string };
    throw new Error(err.response?.data?.error || err.message || 'Failed to fetch invitation.');
  }
};

export const acceptPartnerInvitation = async (tenderId: string, accept: boolean): Promise<void> => {
  try {
    const response = await httpClient.post<{ success: boolean; message: string }>(
      '/acceptPartnerInvitation',
      { tenderId, accept }
    );
    if (!response.success) {
      throw new Error('Failed to process invitation response.');
    }
  } catch (error) {
    const err = error as { response?: { data?: { error?: string } }; message?: string };
    throw new Error(err.response?.data?.error || err.message || 'Failed to process invitation.');
  }
};

export const getUsers = async (filters: UserFilters = {}): Promise<UserResponse> => {
  try {
    const { companyId, searchTerm, ...rest } = filters;
    const payload = {
      ...rest,
      clientId: companyId ?? (rest as Record<string, unknown>).clientId,
      search: searchTerm ?? (rest as Record<string, unknown>).search,
    };
    const response = await httpClient.post<UserResponse>('/getUsers', payload);

    if (response) {
      return response;
    } else {
      throw new Error('Failed to fetch users');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Fetch users failed: ${err.response?.data?.message || err.message}`);
  }
};

export const updateUser = async (updateData: UpdateUserData): Promise<ApiResponse<User>> => {
  try {
    const { userId, companyId, ...rest } = updateData;
    const payload = {
      targetUserId: userId,
      clientId: companyId,
      ...rest,
    };
    const response = await httpClient.post<ApiResponse<User>>('/updateUser', payload);

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to update user');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Update user failed: ${err.response?.data?.message || err.message}`);
  }
};

export const resetPassword = async (targetUserId: string): Promise<PasswordResetResponse> => {
  try {
    const response = await httpClient.post<PasswordResetResponse>('/resetPassword', {
      targetUserId,
    });

    if (response.success) {
      return response;
    } else {
      throw new Error(response.message || 'Failed to reset password');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Password reset failed: ${err.response?.data?.message || err.message}`);
  }
};

export const updateUserRole = async (
  targetUserId: string,
  newRole: string,
  reason?: string
): Promise<ApiResponse<User>> => {
  try {
    const response = await httpClient.post<ApiResponse<User>>('/updateUserRole', {
      targetUserId,
      newRole,
      reason,
    });

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to update user role');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Update user role failed: ${err.response?.data?.message || err.message}`);
  }
};

export const updateAccountDetails = async (
  details: AccountDetailsUpdate
): Promise<ApiResponse<User>> => {
  const response = await httpClient.post<ApiResponse<User>>('/updateAccountDetails', details);
  return response;
};

export const inviteUser = async (userData: {
  email: string;
  role: string;
  companyId?: string;
  displayName?: string;
  teamAssignments?: string[];
  permissions?: string[];
  limits?: UserLimits;
}): Promise<ApiResponse<{ message: string }>> => {
  try {
    const payload = {
      ...userData,
      name: userData.displayName,
    };
    const response = await httpClient.post<ApiResponse<{ message: string }>>(
      '/inviteUser',
      payload
    );

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to invite user');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`User invitation failed: ${err.response?.data?.message || err.message}`);
  }
};

export const revokeInvitation = async (
  tokenId: string
): Promise<ApiResponse<{ message: string }>> => {
  try {
    const response = await httpClient.post<ApiResponse<{ message: string }>>('/revokeInvitation', {
      tokenId,
    });
    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to revoke invitation');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Revoke invitation failed: ${err.response?.data?.message || err.message}`);
  }
};

export const updateInvitation = async (
  invitationId: string,
  updates: {
    role?: string;
    teamId?: string;
    permissions?: string[];
    limits?: UserLimits;
  }
): Promise<ApiResponse<{ message: string }>> => {
  try {
    const payload = {
      invitationId,
      ...updates,
      teamAssignments: updates.teamId ? [updates.teamId] : undefined,
    };
    const response = await httpClient.post<ApiResponse<{ message: string }>>(
      '/updateInvitation',
      payload
    );

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to update invitation');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Update invitation failed: ${err.response?.data?.message || err.message}`);
  }
};

export const allocateUserLimits = async (
  userId: string,
  limits: UserLimits
): Promise<ApiResponse<{ message: string }>> => {
  try {
    const response = await httpClient.post<ApiResponse<{ message: string }>>(
      '/allocateUserLimits',
      {
        userId,
        limits,
      }
    );

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to allocate user limits');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Limit allocation failed: ${err.response?.data?.message || err.message}`);
  }
};

export const deleteUser = async (userId: string): Promise<ApiResponse<{ message: string }>> => {
  try {
    const response = await httpClient.post<ApiResponse<{ message: string }>>('/deleteUser', {
      userId,
    });

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error?.message || 'Failed to delete user');
    }
  } catch (error: unknown) {
    const err = error as Error & { response?: { data?: { message?: string } } };
    throw new Error(`Delete user failed: ${err.response?.data?.message || err.message}`);
  }
};

import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { firestore } from './firebase';

export const getUser = async (userId: string) => {
  const snap = await getDoc(doc(firestore, 'users', userId));
  if (snap.exists()) {
    return { uid: snap.id, ...snap.data() };
  }
  return null;
};

export const updateUserPreferences = async (
  userId: string,
  preferences: Record<string, unknown>
) => {
  const userDocRef = doc(firestore, 'users', userId);
  await updateDoc(userDocRef, preferences);
};

export const getClientRelevanceContext = async (clientId: string): Promise<string> => {
  if (!clientId) {
    return '';
  }
  const fragments: string[] = [];

  try {
    const partnerSnap = await getDoc(doc(firestore, 'partners', clientId));
    if (partnerSnap?.exists()) {
      const pData = partnerSnap.data() as Record<string, unknown>;
      if (typeof pData.capabilitiesSummary === 'string') {
        fragments.push(pData.capabilitiesSummary);
      }
      if (typeof pData.additionalInfo === 'string') {
        fragments.push(pData.additionalInfo);
      }
    }
  } catch {
    // Ignore if partner profile does not exist or is inaccessible
  }

  try {
    const clientSnap = await getDoc(doc(firestore, 'clients', clientId));
    if (clientSnap?.exists()) {
      const cData = clientSnap.data() as Record<string, unknown>;
      if (typeof cData.industry === 'string') {
        fragments.push(cData.industry);
      }
      if (typeof cData.description === 'string') {
        fragments.push(cData.description);
      }
    }
  } catch {
    // Ignore if client document is inaccessible
  }

  try {
    const docsRef = collection(firestore, 'clients', clientId, 'knowledge_documents');
    const q = query(
      docsRef,
      where('collectionType', 'in', ['bidding_company_profile', 'bidding_proposal'])
    );
    const kbSnap = await getDocs(q);
    if (kbSnap?.docs) {
      const profileFragments: string[] = [];
      const proposalFragments: string[] = [];
      for (const d of kbSnap.docs) {
        const data = d.data() as Record<string, unknown>;
        const target =
          data.collectionType === 'bidding_company_profile' ? profileFragments : proposalFragments;
        if (typeof data.globalSummary === 'string') {
          target.push(data.globalSummary);
        }
        if (typeof data.fileName === 'string') {
          target.push(data.fileName.replace(/[_.-]+/g, ' '));
        }
      }
      if (profileFragments.length > 0 || fragments.length > 0) {
        fragments.push(...profileFragments);
      } else {
        fragments.push(...proposalFragments);
      }
    }
  } catch {
    // Ignore if knowledge_documents query is restricted or unindexed
  }

  return fragments.filter(Boolean).join(' ');
};

export interface UserToken {
  uid: string;
  email?: string;
  invitedUserEmail?: string;
  name?: string;
  invitedByName?: string;
  role?: string;
  clientId?: string;
  teamAssignments?: string[];
  limits?: Record<string, unknown>;
  permissions?: string[];
  [key: string]: unknown;
}

export const getUserToken = async (userId: string): Promise<UserToken | null> => {
  const snap = await getDoc(doc(firestore, 'userTokens', userId));
  if (snap.exists()) {
    return { ...snap.data(), uid: snap.id };
  }
  return null;
};

export const getUsersByTeam = async (teamId: string, clientId: string): Promise<User[]> => {
  const q = query(
    collection(firestore, 'users'),
    where('teamId', '==', teamId),
    where('clientId', '==', clientId)
  );
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ ...doc.data(), uid: doc.id }) as User);
};

export const getUsersByClient = async (clientId: string): Promise<User[]> => {
  const q = query(collection(firestore, 'users'), where('clientId', '==', clientId));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ ...doc.data(), uid: doc.id }) as User);
};

export const dismissUserAction = async (userId: string, actionId: string) => {
  const actionRef = doc(firestore, 'users', userId, 'actions', actionId);
  await updateDoc(actionRef, { status: 'dismissed' });
};
